import { useDevice } from "@revolt/common";
import {
  type Accessor,
  type JSX,
  createEffect,
  createSignal,
  on,
  onCleanup,
} from "solid-js";

type Props = JSX.Directives["floating"] & object;

export type FloatingElement = {
  config: () => Props;
  element: HTMLElement;
  hide: () => void;
  show: Accessor<Props | undefined>;
};

const [floatingElements, setFloatingElements] = createSignal<FloatingElement[]>(
  [],
);

export { floatingElements };

/**
 * Register a new floating element
 * @param element element
 */
export function registerFloatingElement(element: FloatingElement) {
  setFloatingElements((elements) => [...elements, element]);
}

/**
 * Un register floating element
 * @param element DOM Element
 */
export function unregisterFloatingElement(element: HTMLElement) {
  setFloatingElements((elements) =>
    elements.filter((entry) => entry.element !== element),
  );
}

/**
 * Add floating elements
 * @param element Element
 * @param accessor Parameters
 */
export function floating(element: HTMLElement, accessor: Accessor<Props>) {
  const config = accessor();
  if (!config) return;

  const { isMobile } = useDevice();

  const [show, setShow] = createSignal<Props | undefined>();
  // DEBUG: createEffect(() => console.info("show:", show()));

  registerFloatingElement({
    config: accessor,
    element,
    show,
    /**
     * Hide the element
     */
    hide() {
      setShow(undefined);
    },
  });

  /**
   * Trigger a floating element
   */
  function trigger(target: keyof Props, desiredState?: boolean) {
    const current = show();
    const config = accessor();

    if (target === "userCard" && config.userCard) {
      if (current?.userCard) {
        setShow(undefined);
      } else if (!current) {
        setShow({ userCard: config.userCard });
      } else {
        setShow(undefined);
        setShow({ userCard: config.userCard });
      }
    }

    if (target === "tooltip" && config.tooltip) {
      if (current?.tooltip) {
        if (desiredState !== true) {
          setShow(undefined);
        }
      } else if (!current) {
        if (desiredState !== false) {
          setShow({ tooltip: config.tooltip });
        }
      }
    }

    if (target === "contextMenu" && config.contextMenu) {
      if (current?.contextMenu) {
        setShow(undefined);
      } else if (!current) {
        setShow({ contextMenu: config.contextMenu });
      } else {
        setShow(undefined);
        setShow({ contextMenu: config.contextMenu });
      }
    }
  }

  /**
   * Handle click events
   */
  function onClick() {
    // TODO: handle shift+click for mention
    trigger("userCard");
  }

  /**
   * Handle context menu click
   */
  function onContextMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    trigger("contextMenu");
  }

  let isTouching = false,
    tTmr: NodeJS.Timeout | undefined;

  /**
   * Handle mouse entering
   */
  function onMouseEnter() {
    if (!isTouching) trigger("tooltip", true);
  }

  /**
   * Handle mouse leaving
   */
  function onMouseLeave() {
    trigger("tooltip", false);
  }

  function onTouch() {
    isTouching = true;
    clearTimeout(tTmr);
    tTmr = setTimeout(() => {
      isTouching = false;
      tTmr = undefined;
    }, 100);
  }

  createEffect(
    on(
      () => accessor().userCard,
      (userCard) => {
        if (userCard) {
          element.style.cursor = "pointer";
          element.style.userSelect = "none";
          element.addEventListener("click", onClick);

          onCleanup(() => element.removeEventListener("click", onClick));
        }
      },
    ),
  );

  createEffect(
    on(
      () => accessor().tooltip,
      (tooltip) => {
        if (tooltip) {
          element.ariaLabel =
            typeof tooltip.content === "string"
              ? tooltip.content
              : tooltip!.aria!;

          element.addEventListener("mouseenter", onMouseEnter);
          element.addEventListener("mouseleave", onMouseLeave);
          element.addEventListener("touchstart", onTouch);
          element.addEventListener("touchend", onTouch);

          onCleanup(() => {
            element.removeEventListener("mouseenter", onMouseEnter);
            element.removeEventListener("mouseleave", onMouseLeave);
            element.removeEventListener("touchstart", onTouch);
            element.removeEventListener("touchend", onTouch);
          });
        }
      },
    ),
  );

  createEffect(
    on(
      () => accessor().contextMenu,
      (contextMenu) => {
        if (contextMenu) {
          const handler = accessor().contextMenuHandler ?? "contextmenu";

          if (handler === "contextmenu" && isMobile) {
            // Custom long-press for touch devices. More forgiving than the
            // long-press-event library (which cancels on >10px of movement).
            const LONG_PRESS_DELAY = 500;
            const LONG_PRESS_MOVE = 15;

            let timer: NodeJS.Timeout | undefined;
            let startX = 0;
            let startY = 0;
            let fired = false;

            function onTouchStart(e: TouchEvent) {
              if (e.touches.length !== 1) return;
              const touch = e.touches[0];
              startX = touch.clientX;
              startY = touch.clientY;
              fired = false;
              clearTimeout(timer);
              timer = setTimeout(() => {
                timer = undefined;
                fired = true;
                trigger("contextMenu");
              }, LONG_PRESS_DELAY);
            }

            function onTouchMove(e: TouchEvent) {
              if (!timer) return;
              const touch = e.touches[0];
              if (
                Math.abs(touch.clientX - startX) > LONG_PRESS_MOVE ||
                Math.abs(touch.clientY - startY) > LONG_PRESS_MOVE
              ) {
                clearTimeout(timer);
                timer = undefined;
              }
            }

            function onTouchEnd() {
              if (timer) {
                clearTimeout(timer);
                timer = undefined;
              }
              // Suppress the synthetic click that follows a long-press so the
              // underlying element doesn't also get activated.
              if (fired) {
                fired = false;
                const suppress = (e: MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  element.removeEventListener("click", suppress, true);
                };
                element.addEventListener("click", suppress, true);
              }
            }

            function onNativeContextMenu(e: Event) {
              e.preventDefault();
            }

            element.addEventListener("touchstart", onTouchStart, {
              passive: true,
            });
            element.addEventListener("touchmove", onTouchMove, {
              passive: true,
            });
            element.addEventListener("touchend", onTouchEnd);
            element.addEventListener("touchcancel", onTouchEnd);
            element.addEventListener("contextmenu", onNativeContextMenu);

            onCleanup(() => {
              clearTimeout(timer);
              element.removeEventListener("touchstart", onTouchStart);
              element.removeEventListener("touchmove", onTouchMove);
              element.removeEventListener("touchend", onTouchEnd);
              element.removeEventListener("touchcancel", onTouchEnd);
              element.removeEventListener("contextmenu", onNativeContextMenu);
            });
          } else {
            element.addEventListener(handler, onContextMenu);

            onCleanup(() => {
              element.removeEventListener(handler, onContextMenu);
            });
          }
        }
      },
    ),
  );

  onCleanup(() => unregisterFloatingElement(element));
}
